import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { usePermissions } from "@/features/auth/hooks/usePermissions"
import { CreateOpportunitySetCard } from "@/features/opportunities/components/sets/CreateOpportunitySetCard/CreateOpportunitySetCard"
import {
  OpportunitySetCard,
  OpportunitySetCardSkeleton,
} from "@/features/opportunities/components/sets/OpportunitySetCard/OpportunitySetCard"
import { INITIAL_OPPORTUNITY_FILTERS } from "@/features/opportunities/constants/opportunity-filters"
import { OPPORTUNITY_DRAG_REORDER_GRID_CLASS } from "@/features/opportunities/lib/opportunity-drag-reorder-grid-classes"
import {
  deriveErrorSetIds,
  deriveGeneratingSetIds,
  deriveGenerationErrors,
  deriveGenerationStartedAt,
} from "@/features/opportunities/lib/set-generation-state"
import type { OpportunitySet } from "@/features/opportunities/types/opportunities.types"
import { opportunitySetsQueryOptions } from "@/lib/queries/opportunity-sets.queries"
import { DASHBOARD_OPPORTUNITY_SETS_LIMIT } from "../../lib/dashboard-opportunity-sets"

export interface DashboardOpportunitySetsSectionProps {
  tenantId: string | null | undefined
  industryLabelByIndex?: Map<string, string>
}

function CreateCardSkeleton() {
  return (
    <div className="flex h-full min-h-40 w-full animate-pulse flex-col rounded-md border border-dashed border-border/60 bg-card p-3.5">
      <div className="mb-4 flex items-start justify-between">
        <div className="h-10 w-10 rounded-lg bg-muted" />
        <div className="h-3 w-8 rounded bg-muted" />
      </div>
      <div className="mb-2 h-4 w-3/5 rounded bg-muted" />
      <div className="mb-4 h-3 w-full rounded bg-muted" />
      <div className="space-y-2">
        <div className="h-3 w-4/5 rounded bg-muted" />
        <div className="h-3 w-3/5 rounded bg-muted" />
        <div className="h-3 w-2/3 rounded bg-muted" />
      </div>
    </div>
  )
}

export function DashboardOpportunitySetsSection({
  tenantId,
  industryLabelByIndex = new Map(),
}: DashboardOpportunitySetsSectionProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const perms = usePermissions()

  const canViewPage = perms.OPPORTUNITIES_VIEW_PAGE.isOn
  const canListSets = perms.OPPORTUNITY_SETS_LIST.isOn
  const canCreateSet = perms.OPPORTUNITY_SETS_CREATE.isOn

  const showSection = canViewPage && (canListSets || canCreateSet)

  const { data: sets = [], isLoading } = useQuery({
    ...opportunitySetsQueryOptions(tenantId),
    enabled: showSection && canListSets && !!tenantId,
  })

  const recentSets = useMemo(
    () => (canListSets ? sets.slice(0, DASHBOARD_OPPORTUNITY_SETS_LIMIT) : []),
    [sets, canListSets],
  )

  const generatingSetIds = useMemo(() => deriveGeneratingSetIds(sets), [sets])
  const errorSetIds = useMemo(() => deriveErrorSetIds(sets), [sets])
  const generationStartedAt = useMemo(() => deriveGenerationStartedAt(sets), [sets])
  const generationErrorBySetId = useMemo(() => deriveGenerationErrors(sets), [sets])
  const isGenerating = generatingSetIds.size > 0

  function handleCreateClick() {
    void navigate({
      to: "/opportunities",
      search: {
        ...INITIAL_OPPORTUNITY_FILTERS,
        tab: "active",
        q: "",
        createSet: true,
      },
    })
  }

  function handleSelectSet(set: OpportunitySet) {
    void navigate({
      to: "/opportunities",
      search: {
        setId: set.id,
        tab: "active",
        q: "",
        ...INITIAL_OPPORTUNITY_FILTERS,
      },
    })
  }

  if (!showSection) return null

  const headingId = "dashboard-opportunity-sets-heading"

  return (
    <section
      className="flex min-w-0 flex-col gap-5 overflow-x-visible"
      aria-labelledby={headingId}
    >
      <h2
        id={headingId}
        className="text-xl font-semibold leading-tight tracking-tight text-balance text-foreground"
      >
        {t("dashboard.opportunitySets.title")}
      </h2>
      <div className={OPPORTUNITY_DRAG_REORDER_GRID_CLASS}>
        {canCreateSet && (
          isLoading && canListSets ? (
            <CreateCardSkeleton />
          ) : (
            <CreateOpportunitySetCard
              onClick={handleCreateClick}
              canCreate={canCreateSet}
              isGenerating={isGenerating}
              showAction
            />
          )
        )}

        {canListSets && isLoading &&
          Array.from({ length: DASHBOARD_OPPORTUNITY_SETS_LIMIT }).map((_, i) => (
            <div key={i} className="h-full min-w-0">
              <OpportunitySetCardSkeleton />
            </div>
          ))}

        {canListSets && !isLoading &&
          recentSets.map((set) => (
            <div key={set.id} className="h-full min-w-0">
              <OpportunitySetCard
                set={set}
                onClick={() => handleSelectSet(set)}
                isGenerating={generatingSetIds.has(set.id)}
                isError={errorSetIds.has(set.id)}
                errorMessage={generationErrorBySetId.get(set.id)}
                industryLabel={
                  set.industry
                    ? industryLabelByIndex.get(set.industry) ?? set.industry
                    : null
                }
                benchmarkIndustryLabel={
                  set.benchmarkIndustry
                    ? industryLabelByIndex.get(set.benchmarkIndustry) ?? set.benchmarkIndustry
                    : null
                }
                generationStartedAt={generationStartedAt.get(set.id)}
              />
            </div>
          ))}
      </div>
    </section>
  )
}
